import moment from 'moment';
import { safeFileName } from '@shared/utils/markdown-export.utils';
import { ChartExportFormat, ChartExportRecord } from './chart-export.models';
import { buildMaiaDesktopMmi } from './maia-desktop-mmi.writer';
import { buildMaiaOnlineCsv } from './maia-online-csv.writer';
import { buildMimChartFile, mimChartFileToJson } from './mim-chart-file';

/** Een klaar exportbestand: inhoud + bestandsnaam + MIME-type voor de download. */
export interface ChartExportFile {
  readonly content: string;
  readonly fileName: string;
  readonly mimeType: string;
}

/** Bestandsnaam-achtervoegsel + extensie per formaat: de ontvanger ziet meteen voor welke software. */
const FILE_NAMING: Record<ChartExportFormat, { readonly label: string; readonly extension: string; readonly mimeType: string }> = {
  'mechanics-in-motion': { label: 'Mechanics in Motion', extension: 'json', mimeType: 'application/json' },
  'maia-online-csv': { label: 'Maia Online', extension: 'csv', mimeType: 'text/csv' },
  'maia-desktop-mmi-v2': { label: 'Maia v2', extension: 'mmi', mimeType: 'application/xml' },
  'maia-desktop-mmi-v3': { label: 'Maia v3', extension: 'mmi', mimeType: 'application/xml' },
  'mybodygraph-mmi': { label: 'MyBodyGraph', extension: 'mmi', mimeType: 'application/xml' },
};

/**
 * Bouwt het exportbestand (puur, zonder download). Eén chart krijgt zijn naam als bestandsnaam,
 * meerdere "charts YYYY-MM-DD"; telkens met de doelsoftware erachter, bv.
 * "Kevin Proost (Maia v3).mmi".
 */
export function buildChartExportFile(
  records: readonly ChartExportRecord[],
  format: ChartExportFormat,
  exportedAt: moment.Moment,
): ChartExportFile {
  const naming = FILE_NAMING[format];
  const base = records.length === 1 ? records[0].name : `charts ${exportedAt.format('YYYY-MM-DD')}`;
  return {
    content: buildContent(records, format, exportedAt),
    fileName: safeFileName(`${base} (${naming.label})`, naming.extension),
    mimeType: naming.mimeType,
  };
}

function buildContent(records: readonly ChartExportRecord[], format: ChartExportFormat, exportedAt: moment.Moment): string {
  switch (format) {
    case 'mechanics-in-motion':
      return mimChartFileToJson(buildMimChartFile(records, exportedAt.toISOString()));
    case 'maia-online-csv':
      return buildMaiaOnlineCsv(records);
    case 'maia-desktop-mmi-v2':
    case 'mybodygraph-mmi':
      return buildMaiaDesktopMmi(records, 'v2', exportedAt);
    case 'maia-desktop-mmi-v3':
      return buildMaiaDesktopMmi(records, 'v3', exportedAt);
  }
}
